import { ChangeDetectionStrategy, Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialog } from '@angular/material/dialog';
import { DividerComponent } from '@shared/components/divider.component';
import { TitleContainerComponent } from '@shared/components/title-container.component';
import { WatchlistsTableComponent } from '../watchlists-table/watchlists-table.component';
import { WatchlistsApiService } from '../services/watchlists.service';
import { WatchlistProject } from '../models/watchlist-project.model';
import { CustomSetFilterComponent } from '@shared/components/grid-components/custom-set-filter/custom-set-filter.component';
import { ProjectDetailDialogComponent } from './project-detail-dialog/project-detail-dialog.component';
import type { GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { projectColDefs } from './project-watchlist-table.config';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize, tap } from 'rxjs';
import { getFilenameFromContentDisposition, saveFile } from '@shared/utils/file.utils';

@Component({
  selector: 'app-watchlist-project',
  imports: [
    TitleContainerComponent,
    MatButtonModule,
    MatIconModule,
    DividerComponent,
    WatchlistsTableComponent,
    MatProgressBarModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './project.component.html',
  styleUrl: './project.component.scss',
  providers: [WatchlistsApiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WatchlistProjectComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  readonly watchlistApiService = inject(WatchlistsApiService);
  readonly projectColDefs = projectColDefs;
  rowData = signal<WatchlistProject[]>([]);
  isLoading = signal<boolean>(false);

  table = viewChild<WatchlistsTableComponent<WatchlistProject>>('watchlistTable');

  isLoadingExport = signal(false);

  gridInitialized = false;

  ngOnInit(): void {
    this.isLoading.set(true);
    this.watchlistApiService.getWatchlistsProject$().subscribe(data => {
      this.isLoading.set(false);
      this.rowData.set(data);
      this.initializeFilterIfReady();
    });
  }

  onRowClicked(event: RowClickedEvent<WatchlistProject>): void {
    if (event.data) {
      this.openProjectDetailDialog(event.data);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onGridReady(event: GridReadyEvent): void {
    this.gridInitialized = true;
    this.initializeFilterIfReady();
  }

  private initializeFilterIfReady(): void {
    if (this.gridInitialized && this.rowData().length > 0) {
      this.table()!.gridApi.getColumnFilterInstance<CustomSetFilterComponent>('sensitivityName');
    }
  }

  private openProjectDetailDialog(project: WatchlistProject): void {
    this.dialog.open(ProjectDetailDialogComponent, {
      data: project,
      width: '70vw',
      maxWidth: '900px',
      height: 'auto',
      maxHeight: '70vh',
      disableClose: false,
      autoFocus: true,
    });
  }

  exportExcel() {
    this.isLoadingExport.set(true);
    this.watchlistApiService
      .exportWatchlistsProject$()
      .pipe(
        tap(response => {
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = getFilenameFromContentDisposition(contentDisposition) ?? 'downloaded-file';
          if (response.body) {
            saveFile(response.body, filename);
          }
        }),
        finalize(() => {
          this.isLoadingExport.set(false);
        })
      )
      .subscribe();
  }
}
